import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { attendanceApi, employeeApi, projectApi, companyApi } from '@/lib/api';
import { clampPageToTotal } from '@/lib/pagination';
import { useAuth } from '@/lib/auth';
import { canImportAttendance } from '@/lib/roles';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { toast } from 'sonner';
import { Upload, Download, Filter, Check, ChevronsUpDown } from 'lucide-react';
import { downloadBlobFile } from '@/lib/exportFile';

const STATUS_COLORS = {
  'P': 'bg-green-50 text-green-700 font-semibold',
  'L': 'bg-yellow-50 text-yellow-700 font-semibold',
  'A': 'bg-red-50 text-red-700 font-semibold',
  'H': 'bg-blue-50 text-blue-700 font-semibold', // Half Day
  'LV': 'bg-purple-50 text-purple-700 font-semibold', // Leave
  'WO': 'bg-muted/50 text-muted-foreground',
  '-': 'text-muted-foreground',
};

/** Short code for leave cells — matches hover leave type (e.g. EL, CL, CO). */
const getLeaveCellText = (leaveType) => {
  const raw = String(leaveType || '').trim();
  if (!raw) return 'LV';
  if (raw === 'CompOff' || raw === 'CO') return 'CO';
  return raw;
};

export default function AttendancePage() {
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [companiesCatalog, setCompaniesCatalog] = useState([]);
  const [allAttendance, setAllAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(String(now.getMonth() + 1).padStart(2, '0'));
  const [selectedYear, setSelectedYear] = useState(String(now.getFullYear()));
  const [empFilter, setEmpFilter] = useState('all');
  const [empFilterOpen, setEmpFilterOpen] = useState(false);
  const [companyFilter, setCompanyFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('Active');
  
  const [importOpen, setImportOpen] = useState(false);
  const [importMonth, setImportMonth] = useState(String(now.getMonth() + 1).padStart(2, '0'));
  const [importYear, setImportYear] = useState(String(now.getFullYear()));
  const [importCompany, setImportCompany] = useState('all');
  const [importProject, setImportProject] = useState('all');
  const [importFile, setImportFile] = useState(null);
  const [importUploading, setImportUploading] = useState(false);
  const [importType, setImportType] = useState('excel');
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  
  const isEmployee = user?.role === 'Employee';
  const canImport = canImportAttendance(user?.role);

  const [totalEmployees, setTotalEmployees] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [allEmployeesForFilter, setAllEmployeesForFilter] = useState([]);
  const [stats, setStats] = useState({
    present: 0,
    absent: 0,
    halfDay: 0,
    onLeave: 0,
    late: 0,
    offDuty: 0,
    employeesUploaded: 0,
  });

  const normalizeArrayResponse = (value) => {
    if (Array.isArray(value)) return value;
    if (Array.isArray(value?.data)) return value.data;
    if (Array.isArray(value?.items)) return value.items;
    return [];
  };

  const normalizeListResponse = (value) => {
    if (Array.isArray(value)) return { items: value, total: value.length, page: 1, pages: 1 };
    if (Array.isArray(value?.items)) return { items: value.items, total: value?.total ?? value.items.length, page: value?.page ?? 1, pages: value?.pages ?? 1 };
    if (Array.isArray(value?.employees)) return { items: value.employees, total: value?.total ?? value.employees.length, page: value?.page ?? 1, pages: value?.pages ?? 1 };
    return { items: [], total: 0, page: 1, pages: 1 };
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const attendanceParams = { month: selectedMonth, year: selectedYear };
        if (!isEmployee && companyFilter !== 'all') attendanceParams.company = companyFilter;
        if (!isEmployee && projectFilter !== 'all') attendanceParams.project = projectFilter;
        if (!isEmployee && empFilter !== 'all') attendanceParams.employeeId = empFilter;
        if (isEmployee && user?.employeeId) attendanceParams.employeeId = user.employeeId;

        const employeeParams = { page, limit: pageSize };
        if (!isEmployee && companyFilter !== 'all') employeeParams.company = companyFilter;
        if (!isEmployee && projectFilter !== 'all') employeeParams.project = projectFilter;
        if (!isEmployee && empFilter !== 'all') employeeParams.employeeId = empFilter;
        if (!isEmployee && statusFilter !== 'all') employeeParams.status = statusFilter;
        if (isEmployee && user?.employeeId) employeeParams.employeeId = user.employeeId;

        const [emps, atts, projs, comps, statsData] = await Promise.all([
          employeeApi.getAll(employeeParams).catch(() => []),
          attendanceApi.get(attendanceParams).catch(() => []),
          projectApi.getAll().catch(() => []),
          companyApi.getAll().catch(() => []),
          attendanceApi.getStats(selectedMonth, selectedYear).catch(() => null),
        ]);
        const normalizedEmps = normalizeListResponse(emps);
        setEmployees(normalizedEmps.items);
        setTotalEmployees(normalizedEmps.total);
        setTotalPages(normalizedEmps.pages);
        setPage((p) => clampPageToTotal(p, normalizedEmps.pages));
        setAllAttendance(normalizeArrayResponse(atts));
        setProjects(normalizeArrayResponse(projs));
        setCompaniesCatalog(normalizeArrayResponse(comps));
        if (statsData) {
          setStats({
            present: statsData?.present ?? 0,
            absent: statsData?.absent ?? 0,
            halfDay: statsData?.halfDay ?? 0,
            onLeave: statsData?.onLeave ?? 0,
            late: statsData?.late ?? 0,
            offDuty: statsData?.offDuty ?? 0,
            employeesUploaded: statsData?.employeesUploaded ?? 0,
          });
        }
      } catch (err) {
        console.error('Failed to fetch data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedMonth, selectedYear, companyFilter, projectFilter, empFilter, statusFilter, page, pageSize, isEmployee, user?.employeeId]);

  useEffect(() => {
    const fetchAllEmps = async () => {
      try {
        const params = { limit: 1000 };
        if (statusFilter !== 'all') params.status = statusFilter;
        const emps = await employeeApi.getAll(params).catch(() => []);
        const normalized = normalizeListResponse(emps);
        setAllEmployeesForFilter(normalized.items);
      } catch {
        setAllEmployeesForFilter([]);
      }
    };
    fetchAllEmps();
  }, [statusFilter]);

  useEffect(() => {
    setPage(1);
  }, [selectedMonth, selectedYear, companyFilter, projectFilter, empFilter, statusFilter]);

  useEffect(() => {
    if (empFilter === 'all' || statusFilter === 'all') return;
    const stillVisible = allEmployeesForFilter.some((e) => (e?.empId || e?.id) === empFilter);
    if (!stillVisible && allEmployeesForFilter.length > 0) setEmpFilter('all');
  }, [statusFilter, allEmployeesForFilter, empFilter]);

  const handleClearFilters = () => {
    const current = new Date();
    setSelectedMonth(String(current.getMonth() + 1).padStart(2, '0'));
    setSelectedYear(String(current.getFullYear()));
    setCompanyFilter('all');
    setProjectFilter('all');
    setEmpFilter('all');
    setStatusFilter('Active');
    setPage(1);
  };

  const companyOptions = useMemo(() => {
    return (companiesCatalog || [])
      .filter((company) => company?.companyId && company?.name)
      .map((company) => ({
        value: company.companyId,
        label: company.name,
      }));
  }, [companiesCatalog]);
  const projectNameById = useMemo(() => {
    const map = {};
    projects.forEach((project) => {
      if (!project?.projectId) return;
      map[project.projectId] = project?.name || project.projectId;
    });
    return map;
  }, [projects]);
  
  const viewableEmps = useMemo(
    () => (allEmployeesForFilter.length > 0 ? allEmployeesForFilter : employees),
    [allEmployeesForFilter, employees]
  );

  const selectedEmpLabel = useMemo(() => {
    if (empFilter === 'all') return 'All Employees';
    const emp = viewableEmps.find((e) => (e.empId || e.id) === empFilter);
    if (!emp) return empFilter;
    return `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || empFilter;
  }, [empFilter, viewableEmps]);
  const daysInMonth = new Date(parseInt(selectedYear), parseInt(selectedMonth), 0).getDate();
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const statusFilteredEmpIds = useMemo(() => {
    if (statusFilter === 'all') return null;
    return new Set(
      (allEmployeesForFilter.length > 0 ? allEmployeesForFilter : employees)
        .map((e) => e?.empId || e?.id)
        .filter(Boolean)
    );
  }, [statusFilter, allEmployeesForFilter, employees]);

  const filteredAttendance = useMemo(() => {
    if (!statusFilteredEmpIds) return allAttendance;
    return allAttendance.filter((a) => statusFilteredEmpIds.has(a?.employeeId));
  }, [allAttendance, statusFilteredEmpIds]);

  const attendanceMap = useMemo(() => {
    const map = {};
    filteredAttendance.forEach(a => {
      const empId = a.employeeId;
      if (!map[empId]) map[empId] = {};
      map[empId][a.date] = a;
    });
    return map;
  }, [filteredAttendance]);

  const handleFileUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    const fileName = file?.name?.toLowerCase() ?? '';
    if (!fileName.endsWith('.xlsx') && !fileName.endsWith('.xls') && !fileName.endsWith('.csv')) {
      toast.error('Please choose a valid .xlsx, .xls or .csv file');
      e.target.value = '';
      return;
    }
    setImportFile(file);
    toast.success(`Selected file: ${file.name}`);
    e.target.value = '';
  };

  const performImport = async (importFn) => {
    if (!importFile) {
      toast.error('Please choose an import file first');
      return;
    }

    try {
      setImportUploading(true);
      const selectedProjectName =
        projects.find((p) => p?.projectId === importProject)?.name ??
        (importProject === 'all' ? '' : importProject);
      const result = await importFn(
        importFile,
        importYear,
        importMonth,
        importCompany === 'all' ? '' : importCompany,
        selectedProjectName
      );
      const importedCount = result?.count ?? result?.importedCount;
      toast.success(importedCount ? `Imported ${importedCount} records` : 'Attendance imported successfully');

      setSelectedMonth(importMonth);
      setSelectedYear(importYear);
      const [atts, statsData] = await Promise.all([
        attendanceApi.get({ month: importMonth, year: importYear }),
        attendanceApi.getStats(importMonth, importYear).catch(() => null),
      ]);
      setAllAttendance(normalizeArrayResponse(atts));
      if (statsData) {
        setStats({
          present: statsData?.present ?? 0,
          absent: statsData?.absent ?? 0,
          halfDay: statsData?.halfDay ?? 0,
          onLeave: statsData?.onLeave ?? 0,
          late: statsData?.late ?? 0,
          offDuty: statsData?.offDuty ?? 0,
          employeesUploaded: statsData?.employeesUploaded ?? 0,
        });
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setImportUploading(false);
    }
    resetImport();
  };

  const importApiByType = {
    excel: attendanceApi.importExcel,
    mn: attendanceApi.importExcelMn,
    pn: attendanceApi.importExcelPn,
  };

  const handleImport = () => performImport(importApiByType[importType] ?? attendanceApi.importExcel);

  const resetImport = () => {
    setImportOpen(false);
    setImportFile(null);
    setImportProject('all');
    setImportType('excel');
  };

  const handleExportAttendance = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const params = {
        month: String(Number(selectedMonth) || selectedMonth),
        year: selectedYear,
      };
      if (!isEmployee && companyFilter !== 'all') params.company = companyFilter;
      if (!isEmployee && projectFilter !== 'all') params.project = projectFilter;

      const searchEmpId = isEmployee ? user?.employeeId : empFilter !== 'all' ? empFilter : null;
      if (searchEmpId) {
        const emp = viewableEmps.find((e) => (e?.empId || e?.id) === searchEmpId);
        const name = emp
          ? `${emp.firstName || ''} ${emp.lastName || ''}`.trim()
          : selectedEmpLabel !== 'All Employees'
            ? selectedEmpLabel
            : '';
        params.search = name || searchEmpId;
      }

      const response = await attendanceApi.export(params);
      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const matchedFileName = disposition.match(/filename="?([^"]+)"?/i)?.[1];
      const fileName =
        matchedFileName ||
        `attendance-${selectedYear}-${String(selectedMonth).padStart(2, '0')}.xlsx`;
      downloadBlobFile(blob, fileName);
      toast.success('Attendance exported');
    } catch (err) {
      toast.error(err?.message || 'Failed to export attendance');
    } finally {
      setExporting(false);
    }
  };

  const displayEmps = employees;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-[#0047AB] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="attendance-page" className="flex flex-col h-[calc(100vh-2rem)] md:h-[calc(100vh-3rem)] overflow-hidden animate-fadeIn">
      <div className="shrink-0 space-y-3 bg-background z-30">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight font-[Manrope]">Attendance</h1>
            <p className="text-muted-foreground mt-1 text-sm sm:text-base">Monthly attendance tracking</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {canImport && (
              <>
                <Button variant="outline" onClick={() => { setImportType('excel'); setImportOpen(true); }} data-testid="import-attendance-btn">
                  <Upload className="w-4 h-4 mr-2" /> Import
                </Button>
                <Button variant="outline" onClick={() => { setImportType('mn'); setImportOpen(true); }} data-testid="import-attendance-2-btn">
                  <Upload className="w-4 h-4 mr-2" /> Import 2
                </Button>
                <Button variant="outline" onClick={() => { setImportType('pn'); setImportOpen(true); }} data-testid="import-attendance-3-btn">
                  <Upload className="w-4 h-4 mr-2" /> Import 3
                </Button>
              </>
            )}
            <Button
              variant="outline"
              onClick={handleExportAttendance}
              disabled={exporting}
              data-testid="export-attendance-btn"
            >
              <Download className="w-4 h-4 mr-2" /> {exporting ? 'Exporting...' : 'Export'}
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 sm:gap-4">
          {[
            { l: 'Present', v: stats.present, c: '#10b981' },
            { l: 'Absent', v: stats.absent, c: '#ef4444' },
            { l: 'Half Day', v: stats.halfDay, c: '#8b5cf6' },
            { l: 'On Leave', v: stats.onLeave, c: '#6366f1' },
            { l: 'Late', v: stats.late, c: '#f59e0b' },
            { l: 'Uploaded Employees', v: stats.employeesUploaded, c: '#64748b' },
          ].map(s => (
            <Card key={s.l} className="border border-border shadow-sm">
              <CardContent className="p-3 sm:p-4 text-center">
                <p className="text-xs uppercase tracking-widest text-muted-foreground">{s.l}</p>
                <p className="text-xl sm:text-2xl font-bold font-mono mt-1" style={{ color: s.c }}>{s.v}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <Card className="border border-border shadow-sm">
          <CardContent className="p-3 sm:p-4 flex flex-wrap items-center gap-3">
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-[140px]" data-testid="month-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Array.from({ length: 12 }, (_, i) => (
                  <SelectItem key={i} value={String(i + 1).padStart(2, '0')}>
                    {new Date(2026, i, 1).toLocaleString('default', { month: 'long' })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="w-[100px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="2025">2025</SelectItem>
                <SelectItem value="2026">2026</SelectItem>
              </SelectContent>
            </Select>
            
            {!isEmployee && (
              <>
                <Select value={companyFilter} onValueChange={setCompanyFilter}>
                  <SelectTrigger className="w-[180px]" data-testid="company-filter">
                    <Filter className="w-3 h-3 mr-2" /><SelectValue placeholder="All Companies" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Companies</SelectItem>
                    {companyOptions.map((company) => (
                      <SelectItem key={company.value} value={company.value}>
                        {company.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={projectFilter} onValueChange={setProjectFilter}>
                  <SelectTrigger className="w-[180px]" data-testid="project-filter">
                    <Filter className="w-3 h-3 mr-2" /><SelectValue placeholder="All Projects" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Projects</SelectItem>
                    {projects.map(p => <SelectItem key={p.projectId} value={p.projectId}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>

                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[140px]" data-testid="status-filter">
                    <Filter className="w-3 h-3 mr-2" /><SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Inactive">Inactive</SelectItem>
                    <SelectItem value="Draft">Draft</SelectItem>
                  </SelectContent>
                </Select>

                <Popover open={empFilterOpen} onOpenChange={setEmpFilterOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      role="combobox"
                      aria-expanded={empFilterOpen}
                      className="w-[220px] justify-between font-normal"
                      data-testid="employee-filter"
                    >
                      <span className="flex items-center gap-2 truncate">
                        <Filter className="w-3 h-3 shrink-0" />
                        <span className="truncate">{selectedEmpLabel}</span>
                      </span>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[280px] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search employee..." />
                      <CommandList>
                        <CommandEmpty>No employee found.</CommandEmpty>
                        <CommandGroup>
                          <CommandItem
                            value="all-employees"
                            onSelect={() => {
                              setEmpFilter('all');
                              setEmpFilterOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                'mr-2 h-4 w-4',
                                empFilter === 'all' ? 'opacity-100' : 'opacity-0'
                              )}
                            />
                            All Employees
                          </CommandItem>
                          {viewableEmps.map((e) => {
                            const empId = e.empId || e.id;
                            const label = `${e.firstName || ''} ${e.lastName || ''}`.trim();
                            return (
                              <CommandItem
                                key={empId}
                                value={`${label} ${empId}`}
                                onSelect={() => {
                                  setEmpFilter(empId);
                                  setEmpFilterOpen(false);
                                }}
                              >
                                <Check
                                  className={cn(
                                    'mr-2 h-4 w-4',
                                    empFilter === empId ? 'opacity-100' : 'opacity-0'
                                  )}
                                />
                                <span className="truncate">{label}</span>
                                <span className="ml-auto text-xs text-muted-foreground font-mono pl-2">
                                  {empId}
                                </span>
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </>
            )}

            <Button
              type="button"
              variant="outline"
              onClick={handleClearFilters}
              data-testid="attendance-clear-filters-btn"
            >
              Clear Filters
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Attendance Grid */}
      <Card className="flex-1 min-h-0 border border-border shadow-sm overflow-hidden flex flex-col mt-3">
        <CardContent className="p-0 flex-1 min-h-0">
          <Table containerClassName="h-full overflow-auto">
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="text-xs uppercase tracking-widest font-medium sticky top-0 left-0 bg-muted z-30 min-w-[160px] shadow-[0_1px_0_0_hsl(var(--border))]">Employee</TableHead>
                {days.map(d => {
                  const date = new Date(parseInt(selectedYear), parseInt(selectedMonth) - 1, d);
                  const isWe = date.getDay() === 0 || date.getDay() === 6;
                  return (
                    <TableHead key={d} className={`text-center text-xs min-w-[36px] px-1 sticky top-0 z-20 shadow-[0_1px_0_0_hsl(var(--border))] ${isWe ? 'bg-muted' : 'bg-muted'}`}>
                      <div>{d}</div>
                      <div className="text-[10px] text-muted-foreground">{date.toLocaleDateString('en', { weekday: 'narrow' })}</div>
                    </TableHead>
                  );
                })}
                <TableHead className="text-xs uppercase tracking-widest text-center min-w-[60px] sticky top-0 z-20 bg-muted shadow-[0_1px_0_0_hsl(var(--border))]">Total</TableHead>
              </TableRow>
            </TableHeader>
              <TableBody>
                {displayEmps.map(emp => {
                  const empId = emp.empId || emp.id;
                  const ea = attendanceMap[empId] || {};
                  const presentDays = Object.values(ea).filter(a => a.status === 'Present').length;
                  const halfDays = Object.values(ea).filter(a => a.status === 'Half Day').length;
                  const leaveDays = Object.values(ea).reduce((s, a) => s + (a.leaveDays || 0), 0);
                  
                  return (
                    <TableRow key={empId} data-testid={`attendance-row-${empId}`}>
                      <TableCell className="sticky left-0 bg-background z-10 border-r">
                        <div className="flex items-center gap-2">
                          {emp.picture ? (
                            <img src={emp.picture} alt="" className="w-6 h-6 rounded-full object-cover" />
                          ) : (
                            <div className="w-6 h-6 rounded-full bg-[#0047AB]/10 flex items-center justify-center text-[10px] font-bold text-[#0047AB]">
                              {emp.firstName?.charAt(0)}
                            </div>
                          )}
                          <div className="min-w-0">
                            <Link
                              to={`/employees/${empId}`}
                              className="text-xs font-medium truncate block hover:text-[#0047AB] hover:underline"
                              data-testid={`attendance-employee-link-${empId}`}
                            >
                              {emp.firstName} {emp.lastName}
                            </Link>
                            {emp.projects?.length > 0 && (
                              <span className="text-[10px] text-muted-foreground">{emp.projects.length} project(s)</span>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      {days.map(d => {
                        const ds = `${selectedYear}-${selectedMonth}-${String(d).padStart(2, '0')}`;
                        const r = ea[ds];
                        const date = new Date(parseInt(selectedYear), parseInt(selectedMonth) - 1, d);
                        const empOffDays = (emp.weeklyOffDays || ['Sunday']).map(wd => wd.toLowerCase());
                        const dayFullName = date.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
                        const isWe = empOffDays.includes(dayFullName);
                        
                        let cellClass = 'text-center text-xs px-1 ';
                        let cellText = '';
                        let title = '';
                        
                        if (isWe && !r) {
                          cellClass += STATUS_COLORS['WO'];
                          cellText = 'WO';
                        } else if (r) {
                          if (r.isLocked) cellClass += 'ring-1 ring-inset ring-gray-300 ';
                          
                          if (r.status === 'Weekly Off') {
                            cellText = 'WO';
                            cellClass += STATUS_COLORS['WO'];
                          } else if (r.status === 'Present') {
                            cellText = r.isLate ? 'L' : 'P';
                            cellClass += r.isLate ? STATUS_COLORS['L'] : STATUS_COLORS['P'];
                          } else if (r.status === 'Absent') {
                            cellText = 'A';
                            cellClass += STATUS_COLORS['A'];
                          } else if (r.status === 'Half Day') {
                            cellText = 'H';
                            cellClass += STATUS_COLORS['H'];
                            title = `Half Day (${r.leaveType || 'Leave'})`;
                          } else if (r.status === 'Leave') {
                            cellText = getLeaveCellText(r.leaveType);
                            cellClass += STATUS_COLORS['LV'];
                            title = `${r.leaveType || 'Leave'} (${r.leaveDays || 1} day)`;
                          }
                          
                          // Four Punch data tooltip
                          const parts = [];
                          if (r.inTime1) parts.push(`In1: ${r.inTime1}`);
                          if (r.outTime1) parts.push(`Out1: ${r.outTime1}`);
                          if (r.inTime2) parts.push(`In2: ${r.inTime2}`);
                          if (r.outTime2) parts.push(`Out2: ${r.outTime2}`);
                          if (r.duration) parts.push(`Dur: ${r.duration}`);
                          if (r.shift) parts.push(`Shift: ${r.shift}`);
                          if (parts.length) title = (title ? title + ' | ' : '') + parts.join(', ');

                          if (r.projectIn || r.projectOut) {
                            const projectInLabel = projectNameById[r.projectIn] || r.projectIn || '-';
                            const projectOutLabel = projectNameById[r.projectOut] || r.projectOut || '-';
                            title += (title ? ' | ' : '') + `Proj In: ${projectInLabel}, Out: ${projectOutLabel}`;
                          }
                        } else {
                          cellText = '-';
                          cellClass += STATUS_COLORS['-'];
                        }
                        
                        return (
                          <TableCell key={d} className={cellClass} title={title}>
                            {cellText}
                          </TableCell>
                        );
                      })}
                      <TableCell className="text-center border-l">
                        <div className="font-mono text-xs font-bold">{presentDays}</div>
                        {(halfDays > 0 || leaveDays > 0) && (
                          <div className="text-[10px] text-muted-foreground">
                            {halfDays > 0 && <span className="text-purple-600">H:{halfDays} </span>}
                            {leaveDays > 0 && <span className="text-red-500">LV:{leaveDays}</span>}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="shrink-0 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mt-3">
        <div className="flex items-center gap-3 flex-wrap">
          <p className="text-sm text-muted-foreground">
            Page {page} of {totalPages} ({totalEmployees} employees)
          </p>
          <div className="flex items-center gap-1.5">
            <span className="text-sm text-muted-foreground">Show</span>
            <Select
              value={String(pageSize)}
              onValueChange={(v) => {
                setPageSize(Number(v));
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[70px] h-8 text-sm" data-testid="attendance-page-size-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground">per page</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
            disabled={page <= 1}
            data-testid="attendance-prev-page-btn"
          >
            Previous
          </Button>
          <Button
            variant="outline"
            onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={page >= totalPages}
            data-testid="attendance-next-page-btn"
          >
            Next
          </Button>
        </div>
      </div>

      {/* Legend */}
      <div className="shrink-0 flex flex-wrap items-center gap-3 text-xs mt-2 pb-1">
        <span className="text-muted-foreground font-medium">Legend:</span>
        <Badge variant="outline" className={STATUS_COLORS['P']}>P = Present</Badge>
        <Badge variant="outline" className={STATUS_COLORS['L']}>L = Late</Badge>
        <Badge variant="outline" className={STATUS_COLORS['A']}>A = Absent</Badge>
        <Badge variant="outline" className={STATUS_COLORS['H']}>H = Half Day</Badge>
        <Badge variant="outline" className={STATUS_COLORS['LV']}>EL/CL/ML/CO = Leave type</Badge>
        <Badge variant="outline" className={STATUS_COLORS['WO']}>WO = Week Off</Badge>
      </div>

      {/* Import Dialog - Four Punch Format */}
      <Dialog open={importOpen} onOpenChange={(v) => { if (!v) resetImport(); else setImportOpen(true); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">Import Attendance (Four Punch)</DialogTitle>
            <DialogDescription>Upload monthly attendance Excel exported from biometric system</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Month</label>
                  <Select value={importMonth} onValueChange={setImportMonth}>
                    <SelectTrigger data-testid="import-month"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 12 }, (_, i) => (
                        <SelectItem key={i} value={String(i + 1).padStart(2, '0')}>
                          {new Date(2026, i, 1).toLocaleString('default', { month: 'long' })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Year</label>
                  <Select value={importYear} onValueChange={setImportYear}>
                    <SelectTrigger data-testid="import-year"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[2024, 2025, 2026, 2027].map(y => (
                        <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Company</label>
                  <Select value={importCompany} onValueChange={setImportCompany}>
                    <SelectTrigger data-testid="import-company"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Companies</SelectItem>
                      {companyOptions.map((company) => (
                        <SelectItem key={company.value} value={company.value}>
                          {company.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Project</label>
                  <Select value={importProject} onValueChange={setImportProject}>
                    <SelectTrigger data-testid="import-project"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Projects</SelectItem>
                      {projects.map((p) => (
                        <SelectItem key={p.projectId} value={p.projectId}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
                <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm font-medium mb-1">Upload Attendance File</p>
                <p className="text-xs text-muted-foreground mb-4">Supported: .xlsx, .xls, .csv</p>
                <div className="relative inline-block">
                  <input 
                    type="file" 
                    accept=".xlsx,.xls,.csv" 
                    onChange={handleFileUpload} 
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" 
                    data-testid="import-file-input" 
                  />
                  <Button variant="outline" data-testid="select-file-btn">Choose File</Button>
                </div>
                {importFile && (
                  <p className="mt-3 text-xs text-muted-foreground break-all">
                    Selected: <span className="font-medium">{importFile.name}</span>
                  </p>
                )}
              </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={resetImport}>Cancel</Button>
            <Button
              onClick={handleImport}
              disabled={!importFile || importUploading}
              className="bg-[#0047AB] hover:bg-[#003d96] text-white"
              data-testid="confirm-import-btn"
            >
              {importUploading
                ? 'Importing...'
                : importType === 'mn'
                  ? 'Import 2'
                  : importType === 'pn'
                    ? 'Import 3'
                    : 'Import Attendance'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
